import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../SessionProvider';
import {
  ExportOutputFormat,
  getReport,
  getReportExportFormats,
  getReportTemplates,
  ParsedReportParams,
  ReportTemplate,
} from './getReports';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';
import {
  CloneReport,
  CreateReportTemplateAndReport,
  ReportBackendTypes,
  ReportTypeToParams,
  ShareReport,
  UpdateScheduleForReportTemplate,
} from './createReports';
import { deleteReport, deleteReportTemplate, abortJob } from './deleteReports';
import { convertFieldsToDates } from '@ais-ui/utils';

/**
 * Date fields in report params that need to be converted from strings to Date objects.
 * Used by useGetReport and popup report pages to ensure consistent date handling.
 */
export const REPORT_DATE_FIELDS = [
  'info.Created',
  'info.End time',
  'info.Start time',
] as const;

export const useGetReportTemplates = (
  shouldRefetch: boolean,
): UseQueryResult<ReportTemplate[]> => {
  const { dbSession } = useSession();
  return useQuery(
    {
      queryKey: ['getReportTemplates'],
      queryFn: async () => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }

        const result = await getReportTemplates(dbSession); // Ensure async function

        if (result) {
          for (const template of result) {
            convertFieldsToDates(template, [
              'tplnewestcreationdate',
              'jobs.creationdate',
              'schedule.endTime',
            ]);
          }
        }

        return result;
      },
      // We do not want to refetch all the time as it causes re-renders
      refetchInterval: shouldRefetch ? 2000 : false,
    },
    'reports:templates',
  );
};

export const useGetReport = (
  jobId: string,
  _timeZone: string,
): UseQueryResult<ParsedReportParams> => {
  const { dbSession } = useSession();
  return useQuery(
    {
      queryKey: ['getReport', jobId],
      queryFn: async () => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        const result = await getReport(dbSession, jobId);

        if (result) {
          convertFieldsToDates(result, [...REPORT_DATE_FIELDS]);
        }

        return result;
      },
      enabled: !!jobId,
    },
    'reports:get',
  );
};

export const useGetReportExportFormats = (
  type: string,
): UseQueryResult<ExportOutputFormat[]> => {
  const { dbSession } = useSession();

  return useQuery(
    {
      queryKey: ['getReportExportFormats', type],
      queryFn: () => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return getReportExportFormats(dbSession, type);
      },
      enabled: !!dbSession && !!type,
    },
    'reports:exportFormats',
  );
};

type ReportInput<T extends ReportBackendTypes> = {
  type: T;
  params: ReportTypeToParams[T];
};

export const useCreateReportTemplateAndReport = <
  T extends ReportBackendTypes,
>(): UseMutationResult<string, Error, ReportInput<T>, unknown> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: ({ type, params }: ReportInput<T>) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return CreateReportTemplateAndReport(dbSession, type, params);
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getReportTemplates'] }),
    },
    'report:create_template',
  );
};

export const useDisableReportSchedule = (): UseMutationResult<
  string,
  Error,
  object,
  unknown
> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: (params: object) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return UpdateScheduleForReportTemplate(dbSession, params);
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getReportTemplates'] }),
    },
    'report:update_schedule',
  );
};

export const useCloneReport = <
  T extends ReportBackendTypes,
>(): UseMutationResult<string, Error, ReportInput<T>, unknown> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: ({ type, params }: ReportInput<T>) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return CloneReport(dbSession, type, params);
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getReportTemplates'] }),
    },
    'report:clone',
  );
};

export const useShareReport = <
  T extends ReportBackendTypes,
>(): UseMutationResult<string, Error, ReportInput<T>, unknown> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: ({ type, params }: ReportInput<T>) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return ShareReport(dbSession, type, params);
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getReportTemplates'] }),
    },
    'report:share',
  );
};

export const useDeleteReportTemplate = (): UseMutationResult<
  null,
  Error,
  string,
  unknown
> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: (uuid: string) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return deleteReportTemplate(dbSession, uuid);
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getReportTemplates'] }),
    },
    'report:delete_template',
  );
};

export const useDeleteReport = (): UseMutationResult<
  null,
  Error,
  string,
  unknown
> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: (uuid: string) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return deleteReport(dbSession, uuid);
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getReportTemplates'] }),
    },
    'report:delete',
  );
};

export const useAbortJob = (): UseMutationResult<
  null,
  Error,
  string,
  unknown
> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: (jobUuid: string) => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        return abortJob(dbSession, jobUuid);
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['getReportTemplates'] }),
    },
    'report:abort',
  );
};
